import React, { useState, useEffect, Fragment } from 'react';
import { Link, Redirect, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Routes } from 'common/enums';
import { encodeImage, setDocumentTitle } from 'common/utils';
import { ArrowLeftTwoTone } from 'common/icons';
import { Category, Item, Tax, AppActions, ItemType, DisplayType } from 'common/types';
import { getItemEntity, getItemById } from 'common/assets';
import { CategoryPicker } from '../../components';
import { CommonLayout } from '../index';
import { randomColor } from 'common/utils/colors';
import styles from './itemEditor.module.css';

type ItemEditorProps = {
  items: Item[];
  categories: Category[];
  taxes: Tax[];
  actions: AppActions;
};

const ItemEditor: React.FC<ItemEditorProps> = ({ items, categories, taxes, actions }) => {
  const { id } = useParams<{ id: string }>();
  const [t] = useTranslation();

  useEffect(() => {
    const title = [t('admin.title'), t(id ? 'admin.itemEditor.editItemTitle' : 'admin.itemEditor.addItemTitle')];
    setDocumentTitle(title);
  }, [t, id]);

  const initialState = id ? getItemById(items, id) : getItemEntity();

  const [item, setItem] = useState<Item>(initialState);
  const [redirect, setRedirect] = useState('');
  const [isPickerShown, togglePicker] = useState(false);
  const [isItemPickerShown, toggleItemPicker] = useState(false);

  // Helpers
  const selectedCategoryName = categories.find((entity) => entity.id === item.parentId)?.name || t('common.homeScreen');
  const selectedParentItemName = items.find((entity) => entity.id === item.parentItemId)?.name || '';
  const updateItem = (data: Partial<Item>) => setItem({ ...item, ...data });
  // Validate that variant items must have a parent item selected
  const isInvalid = !item.name || (item.type === 'variant' && !item.parentItemId);

  const handleOnChangeCategoryPicker = (categoryId: string | null) => updateItem({ parentId: categoryId });
  const handleOnChangeParentItemPicker = (parentItemId: string | null) => {
    if (parentItemId && item.type === 'variant') {
      // Para variantes, verificar que el padre sea de tipo 'group'
      const parentItem = items.find(i => i.id === parentItemId);
      if (parentItem && parentItem.type === 'group') {
        // Guardar el ID del padre y también su nombre
        updateItem({
          parentItemId,
          itemParentName: parentItem.name
        });
      } else {
        // Si el padre no es de tipo 'group', no permitir la selección
        alert(t('admin.itemEditor.variantParentMustBeGroup', 'El producto padre debe ser de tipo Grupo'));
      }
    } else {
      // Si no es variante o no tiene padre, limpiar itemParentName
      updateItem({
        parentItemId,
        itemParentName: null
      });
    }
  };

  const handleOnClickOnPrimaryBtn = () => {
    // Asegurar que los valores sean consistentes con el tipo de item
    const _item = { ...item, color: randomColor() };

    // Si es un grupo, asegurar que precio y precio de costo sean 0
    if (_item.type === 'group') {
      _item.price = 0;
      _item.costPrice = 0;
      _item.parentItemId = null;
      _item.itemParentName = null;
    }

    // Si es de tipo only, asegurar que no tenga padre
    if (_item.type === 'only') {
      _item.parentItemId = null;
      _item.itemParentName = null;
    }

    // Si es una variante, asegurar que tenga un padre de tipo grupo
    if (_item.type === 'variant') {
      if (!_item.parentItemId) {
        // Mostrar mensaje de error si no se seleccionó un padre para la variante
        alert(t('admin.itemEditor.variantRequiresParent', 'Las variantes deben tener un producto padre seleccionado'));
        return; // No continuar con el submit
      }
      
      const parentItem = items.find(i => i.id === _item.parentItemId);
      if (!parentItem || parentItem.type !== 'group') {
        _item.parentItemId = null;
        _item.itemParentName = null;
        alert(t('admin.itemEditor.variantParentMustBeGroup', 'El producto padre debe ser de tipo Grupo'));
        return; // No continuar con el submit
      } else {
        // Asegurar que itemParentName siempre tenga el nombre del grupo padre
        _item.itemParentName = parentItem.name;
      }
    }

    if (!id) actions.item.add(_item);
    else actions.item.update(_item);
    setRedirect(Routes.AdminItemList);
  };

  if (redirect) return <Redirect to={redirect} />;

  const renderTaxList = () => {
    const taxList = taxes.filter((tax) => tax.isEnabled && !tax.isDeleted);
    if (!taxList.length) return null;
    return (
      <div className="mb-4">
        <div className="form-label">{t('admin.itemEditor.taxesLabel')}</div>
        {taxList.map((tax) => (
          <div key={`ItemEditorTax_${tax.id}`} className={styles.switch}>
            <input
              type="checkbox"
              id={`ItemEditorTax_${tax.id}`}
              checked={item.taxes.includes(tax.id)}
              onChange={(evt) =>
                updateItem({
                  taxes: evt.target.checked
                    ? [...item.taxes, tax.id]
                    : [...item.taxes.filter((item) => item !== tax.id)],
                })
              }
            />
            <label htmlFor={`ItemEditorTax_${tax.id}`}>{`${tax.name} (${tax.precentage}%)`}</label>
          </div>
        ))}
      </div>
    );
  };

  const renderHead = (
    <Fragment>
      <Link className="btn btn-link" to={Routes.AdminItemList}>
        <ArrowLeftTwoTone />
      </Link>
      <div className={styles.title}>
        {id ? t('admin.itemEditor.editItemTitle') : t('admin.itemEditor.addItemTitle')}
      </div>
    </Fragment>
  );

  const renderBody = (
    <form>
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorName">
          {t('admin.itemEditor.nameLabel')}
        </label>
        <input
          id="ItemEditorName"
          type="text"
          className="form-control"
          value={item.name}
          onChange={(evt) => updateItem({ name: evt.target.value })}
        />
      </div>
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorDescription">
          {t('admin.itemEditor.descriptionLabel')}
        </label>
        <input
          id="ItemEditorDescription"
          type="text"
          className="form-control"
          value={item.description}
          onChange={(evt) => updateItem({ description: evt.target.value })}
        />
      </div>
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorName">
          {t('admin.itemEditor.barcodeLabel')}
        </label>
        <input
          id="ItemEditorName"
          type="text"
          className="form-control"
          value={item.barcode}
          onChange={(evt) => updateItem({ barcode: evt.target.value })}
        />
      </div>
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorCategory">
          {t('admin.itemEditor.categoryLabel')}
        </label>
        <div className={styles.controlGroup}>
          <input
            readOnly
            type="text"
            id="ItemEditorCategory"
            className={`${styles.controlInput} ${isPickerShown ? 'focus' : ''}`}
            value={selectedCategoryName}
            onClick={() => togglePicker(true)}
          />
          {isPickerShown && (
            <CategoryPicker
              className={styles.picker}
              categories={categories}
              parent={item.parentId}
              selected={item.id}
              onChange={handleOnChangeCategoryPicker}
              onClose={() => togglePicker(false)}
            />
          )}
        </div>
      </div>
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorType">
          {t('admin.itemEditor.typeLabel')}
        </label>
        <div className={styles.controlGroup}>
          <select
            id="ItemEditorType"
            className="form-control"
            value={item.type}
            onChange={(evt) => {
              const newType = evt.target.value as ItemType;
              const updates: Partial<Item> = { type: newType };

              // Resetear valores según el tipo
              if (newType === 'group') {
                // Los grupos no tienen precio, precio de costo ni padre
                updates.price = 0;
                updates.costPrice = 0;
                updates.parentItemId = null;
              } else if (newType === 'only') {
                // Los items 'only' no tienen padre
                updates.parentItemId = null;
              } else if (newType === 'variant' && item.parentItemId) {
                // Verificar que el padre actual sea de tipo group
                const parentItem = items.find(i => i.id === item.parentItemId);
                if (!parentItem || parentItem.type !== 'group') {
                  updates.parentItemId = null;
                }
              }

              updateItem(updates);
            }}
          >
            <option value="only">{t('admin.itemEditor.typeOnly')}</option>
            <option value="group">{t('admin.itemEditor.typeGroup')}</option>
            <option value="variant">{t('admin.itemEditor.typeVariant')}</option>
          </select>
        </div>
      </div>
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorDisplayMode">
          {t('admin.itemEditor.displayModeLabel', 'Display Mode')}
        </label>
        <select id="ItemEditorDisplayMode" className="form-control" value={item.displayType} onChange={(evt) => updateItem({ displayType: evt.target.value as DisplayType })}>
          <option value="list">{t('admin.itemEditor.displayTypeList', 'List')}</option>
          <option value="grid">{t('admin.itemEditor.displayTypeGrid', 'Grid')}</option>
          <option value="stepper">{t('admin.itemEditor.displayTypeStepper', 'Stepper')}</option>
          <option value="catalog">{t('admin.itemEditor.displayTypeCatalog', 'Catalog')}</option>
        </select>
      </div>
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorParentItem">
          {t('admin.itemEditor.parentItemLabel', 'Parent Item')}
          {item.type === 'variant' && <span className="text-danger">*</span>}
          {item.type !== 'variant' && (
            <small className="ms-2 text-muted">
              ({t('admin.itemEditor.onlyForVariants', 'Solo para variantes')})
            </small>
          )}
        </label>
        <div className={styles.controlGroup}>
          <select
            id="ItemEditorParentItem"
            className="form-control"
            value={item.parentItemId || ''}
            onChange={(evt) => handleOnChangeParentItemPicker(evt.target.value || null)}
            disabled={item.type !== 'variant'}
          >
            <option value="">{t('admin.itemEditor.noParentItem', 'No Parent Item')}</option>
            {items
              .filter((i) => !i.isDeleted && !i.isHidden && i.id !== item.id && (item.type !== 'variant' || i.type === 'group'))
              .map((i) => (
                <option key={`parent-item-${i.id}`} value={i.id}>
                  {i.name}{i.type === 'group' ? ` (${t('admin.itemEditor.typeGroup')})` : ''}
                </option>
              ))}
          </select>
        </div>
      </div>
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorPrice">
          {t('admin.itemEditor.priceLabel')}
          {item.type === 'group' && (
            <small className="ms-2 text-muted">
              ({t('admin.itemEditor.notApplicableForGroups', 'No aplicable para grupos')})
            </small>
          )}
        </label>
        <input
          type="number"
          className="form-control"
          id="ItemEditorPrice"
          placeholder={t('admin.itemEditor.priceLabel')}
          value={item.price}
          onChange={(evt) => updateItem({ price: parseFloat(evt.target.value) })}
          disabled={item.type === 'group'}
          readOnly={item.type === 'group'}
        />
      </div>
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorCostPrice">
          {t('admin.itemEditor.costPriceLabel')}
          {item.type === 'group' && (
            <small className="ms-2 text-muted">
              ({t('admin.itemEditor.notApplicableForGroups', 'No aplicable para grupos')})
            </small>
          )}
        </label>
        <input
          type="number"
          className="form-control"
          id="ItemEditorCostPrice"
          placeholder={t('admin.itemEditor.costPriceLabel')}
          value={item.costPrice}
          onChange={(evt) => updateItem({ costPrice: parseFloat(evt.target.value) })}
          disabled={item.type === 'group'}
          readOnly={item.type === 'group'}
        />
      </div>
      {/* <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorColor">
          {t('admin.itemEditor.colorLabel')}
        </label>
        <select
          id="ItemEditorColor"
          className="form-control"
          value={item.color || 'transparent'}
          onChange={(evt) => updateItem({ color: evt.target.value })}
        >
          <optgroup label="Please specify the color">
            <option value="transparent">Default</option>
            <option value="salmon">Salmon</option>
            <option value="red">Red</option>
            <option value="coral">Coral</option>
            <option value="tomato">Tomato</option>
            <option value="gold">Gold</option>
            <option value="orange">Orange</option>
          </optgroup>
        </select>
      </div> */}
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorColor">
          {t('admin.itemEditor.imageLabel')}
        </label>
        {!item.picture && (
          <input
            type="file"
            className="form-control"
            onChange={(evt) => encodeImage(evt, (picture) => updateItem({ picture }))}
          />
        )}
        {!!item.picture && (
          <button className={styles.secondaryAction} onClick={() => updateItem({ picture: '' })}>
            {t('common.remove')}
          </button>
        )}
      </div>
      {renderTaxList()}
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorIsPublic">
          {t('admin.itemEditor.isPublicLabel')}
        </label>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="ItemEditorIsPublic"
            checked={item.isPublic}
            onChange={(evt) => updateItem({ isPublic: evt.target.checked })}
          />
          <label className="form-check-label" htmlFor="ItemEditorIsPublic">
            <small>{t('admin.itemEditor.isPublicDescription')}</small>
          </label>
        </div>
      </div>
      <div className="mb-4">
        <label className="form-label" htmlFor="ItemEditorOffer">
          {t('admin.itemEditor.isOfferLabel')}
        </label>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="ItemEditorOffer"
            checked={item.offer}
            onChange={(evt) => updateItem({ offer: evt.target.checked, sortOrderOffer: Date.now() })}
          />
          <label className="form-check-label" htmlFor="ItemEditorOffer">
            <small>{t('admin.itemEditor.isOfferDescription')}</small>
          </label>
        </div>
      </div>
      {/* Campos de contacto para items de tipo 'group' */}
      {item.type === 'group' && (
        <>
          <div className="mb-4 mt-4">
            <h4>{t('admin.itemEditor.contactInfoTitle', 'Información de contacto')}</h4>
            <p><small>{t('admin.itemEditor.contactInfoDescription', 'Estos campos son específicos para items de tipo Grupo')}</small></p>
          </div>
          <div className="mb-4">
            <label className="form-label" htmlFor="ItemEditorPhoneNumber">
              {t('admin.itemEditor.phoneNumberLabel', 'Número de teléfono')}
            </label>
            <input
              id="ItemEditorPhoneNumber"
              type="tel"
              className="form-control"
              value={item.phoneNumber || ''}
              onChange={(evt) => updateItem({ phoneNumber: evt.target.value })}
              placeholder={t('admin.itemEditor.phoneNumberPlaceholder', 'Ej: +54 9 11 1234-5678')}
            />
          </div>
          <div className="mb-4">
            <label className="form-label" htmlFor="ItemEditorAddress">
              {t('admin.itemEditor.addressLabel', 'Dirección')}
            </label>
            <input
              id="ItemEditorAddress"
              type="text"
              className="form-control"
              value={item.address || ''}
              onChange={(evt) => updateItem({ address: evt.target.value })}
              placeholder={t('admin.itemEditor.addressPlaceholder', 'Ej: Av. Corrientes 1234, CABA')}
            />
          </div>
          <div className="mb-4">
            <label className="form-label">
              {t('admin.itemEditor.locationLabel', 'Ubicación (Latitud/Longitud)')}
            </label>
            <div className="row g-2">
              <div className="col-6">
                <input
                  id="ItemEditorLocationLat"
                  type="number"
                  step="any"
                  className="form-control"
                  value={item.location?.lat || ''}
                  onChange={(evt) => updateItem({
                    location: {
                      lat: parseFloat(evt.target.value) || 0,
                      lng: item.location?.lng || 0
                    }
                  })}
                  placeholder={t('admin.itemEditor.latitudePlaceholder', 'Latitud')}
                />
              </div>
              <div className="col-6">
                <input
                  id="ItemEditorLocationLng"
                  type="number"
                  step="any"
                  className="form-control"
                  value={item.location?.lng || ''}
                  onChange={(evt) => updateItem({
                    location: {
                      lat: item.location?.lat || 0,
                      lng: parseFloat(evt.target.value) || 0
                    }
                  })}
                  placeholder={t('admin.itemEditor.longitudePlaceholder', 'Longitud')}
                />
              </div>
            </div>
            <div className="mt-2">
              <small className="text-muted">{t('admin.itemEditor.locationDescription', 'Coordenadas para geolocalización')}</small>
            </div>
          </div>
        </>
      )}
      <div className="mb-4">
        <div className="form-label" />
        <button className={styles.primaryAction} disabled={isInvalid} onClick={handleOnClickOnPrimaryBtn}>
          {id ? t('common.update') : t('common.add')}
        </button>
      </div>
    </form>
  );

  return <CommonLayout head={renderHead} body={renderBody} />;
};

export default ItemEditor;
